/**
 * Public Peer Evaluation Page
 *
 * Standalone page for external evaluators to submit peer feedback.
 * Accessible via unique token link without authentication.
 *
 * Route: /evaluatie/:token (outside AuthProvider)
 */

import { useState, useCallback, useMemo, useEffect, useRef, memo } from 'react';
import { useParams } from 'react-router-dom';
import {
  usePublicPeerEvaluation,
  useSubmitPublicPeerEvaluation,
} from '../peer-evaluations/usePeerEvaluations';
import type {
  PublicTask,
  PublicCompetency,
  SubmitPeerEvaluationInput,
} from '../../lib/peerEvaluationApi';

// ============================================
// CONSTANTS
// ============================================

const MAX_GENERAL_REMARKS_LENGTH = 2000;

// ============================================
// TYPES
// ============================================

type PageStatus = 'loading' | 'ready' | 'submitting' | 'submitted' | 'invalid' | 'error';

interface FormState {
  taskRemarks: Record<number, string>;
  competencyRemarks: Record<number, string>;
  generalRemarks: string;
}

// ============================================
// SUB-COMPONENTS
// ============================================

/**
 * Loading state display
 */
function LoadingState() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50"
      data-testid="loading-state"
      aria-busy="true"
    >
      <div className="text-center">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"
          role="status"
          aria-label="Laden"
        />
        <p className="mt-4 text-gray-600">Formulier laden...</p>
      </div>
    </div>
  );
}

/**
 * Invalid token or already submitted state
 */
function InvalidState() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
      data-testid="invalid-state"
    >
      <div className="text-center max-w-md">
        <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-red-100">
          <svg
            className="h-10 w-10 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <h1 className="mt-4 text-2xl font-bold text-gray-900">Link niet geldig</h1>
        <p className="mt-2 text-gray-600">
          Deze evaluatie link is niet (meer) geldig of is al ingevuld.
        </p>
        <p className="mt-4 text-sm text-gray-500">Je kunt dit venster sluiten.</p>
      </div>
    </div>
  );
}

/**
 * General error state
 */
function ErrorState({ message }: { message: string }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
      data-testid="error-state"
    >
      <div className="text-center max-w-md">
        <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-yellow-100">
          <svg
            className="h-10 w-10 text-yellow-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="mt-4 text-2xl font-bold text-gray-900">Er ging iets mis</h1>
        <p className="mt-2 text-gray-600">{message}</p>
        <p className="mt-4 text-sm text-gray-500">
          Probeer de pagina te vernieuwen of neem contact op met de aanvrager.
        </p>
      </div>
    </div>
  );
}

/**
 * Success/thank you state after submission
 */
function SuccessState() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
      data-testid="success-state"
    >
      <div className="text-center max-w-md">
        <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100">
          <svg
            className="h-10 w-10 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h1 className="mt-4 text-2xl font-bold text-gray-900">Bedankt voor je feedback!</h1>
        <p className="mt-2 text-gray-600">
          Je evaluatie is succesvol ingediend en wordt meegenomen in de beoordeling.
        </p>
        <p className="mt-4 text-sm text-gray-500">Je kunt dit venster sluiten.</p>
      </div>
    </div>
  );
}

/**
 * Task evaluation section - styled to match evaluation wizard
 */
const TaskSection = memo(function TaskSection({
  tasks,
  remarks,
  onRemarksChange,
}: {
  tasks: PublicTask[];
  remarks: Record<number, string>;
  onRemarksChange: (taskId: number, value: string) => void;
}) {
  if (tasks.length === 0) return null;

  return (
    <section className="mb-8" data-testid="tasks-section">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
        Taken ({tasks.length})
      </h2>
      <div className="space-y-4">
        {tasks.map((task) => {
          return (
            <div
              key={task.taskId}
              className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
              data-testid={`task-item-${task.taskId}`}
            >
              {/* Task header - matching evaluation wizard style */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900" id={`task-title-${task.taskId}`}>
                      {task.name}
                    </h3>
                    {task.beschrijving && (
                      <p className="mt-1 text-sm text-gray-500" id={`task-desc-${task.taskId}`}>
                        {task.beschrijving}
                      </p>
                    )}
                  </div>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full whitespace-nowrap ml-4">
                    Taak
                  </span>
                </div>
              </div>

              <div className="px-6 py-4">
                {/* Jouw evaluatie textarea */}
                <div>
                  <label
                    htmlFor={`task-remarks-${task.taskId}`}
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Jouw evaluatie
                  </label>
                  <textarea
                    id={`task-remarks-${task.taskId}`}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Geef je feedback over deze taak..."
                    value={remarks[task.taskId] || ''}
                    onChange={(e) => onRemarksChange(task.taskId, e.target.value)}
                    data-testid={`task-remarks-${task.taskId}`}
                    aria-describedby={task.beschrijving ? `task-title-${task.taskId} task-desc-${task.taskId}` : `task-title-${task.taskId}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
});

/**
 * Competency evaluation section - styled to match evaluation wizard
 */
const CompetencySection = memo(function CompetencySection({
  competencies,
  remarks,
  onRemarksChange,
}: {
  competencies: PublicCompetency[];
  remarks: Record<number, string>;
  onRemarksChange: (competencyId: number, value: string) => void;
}) {
  if (competencies.length === 0) return null;

  return (
    <section className="mb-8" data-testid="competencies-section">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
        Competenties ({competencies.length})
      </h2>
      <div className="space-y-4">
        {competencies.map((comp) => {
          // Determine badge color based on competency type
          const badgeStyles = comp.type === 'basis'
            ? 'bg-blue-100 text-blue-700'
            : 'bg-purple-100 text-purple-700';
          const badgeLabel = comp.type === 'basis' ? 'Basiscompetentie' : 'Vakcompetentie';

          return (
            <div
              key={comp.competencyId}
              className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
              data-testid={`competency-item-${comp.competencyId}`}
            >
              {/* Competency header - matching evaluation wizard style */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900" id={`comp-title-${comp.competencyId}`}>
                      {comp.name}
                    </h3>
                    {comp.beschrijving && (
                      <p className="mt-1 text-sm text-gray-500" id={`comp-desc-${comp.competencyId}`}>
                        {comp.beschrijving}
                      </p>
                    )}
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full whitespace-nowrap ml-4 ${badgeStyles}`}>
                    {badgeLabel}
                  </span>
                </div>
              </div>

              <div className="px-6 py-4">
                {/* Jouw evaluatie textarea */}
                <div>
                  <label
                    htmlFor={`competency-remarks-${comp.competencyId}`}
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Jouw evaluatie
                  </label>
                  <textarea
                    id={`competency-remarks-${comp.competencyId}`}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Geef je feedback over deze competentie..."
                    value={remarks[comp.competencyId] || ''}
                    onChange={(e) => onRemarksChange(comp.competencyId, e.target.value)}
                    data-testid={`competency-remarks-${comp.competencyId}`}
                    aria-describedby={comp.beschrijving ? `comp-title-${comp.competencyId} comp-desc-${comp.competencyId}` : `comp-title-${comp.competencyId}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
});

/**
 * General remarks section with character counter
 */
function GeneralRemarksSection({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const remainingChars = MAX_GENERAL_REMARKS_LENGTH - value.length;
  const isNearLimit = remainingChars < 200;

  return (
    <section className="mb-8" data-testid="general-remarks-section">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
        Algemene Opmerkingen
      </h2>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <label
          htmlFor="general-remarks"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Aanvullende feedback <span className="text-gray-400 font-normal">(optioneel)</span>
        </label>
        <textarea
          id="general-remarks"
          rows={5}
          maxLength={MAX_GENERAL_REMARKS_LENGTH}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
          placeholder="Deel hier eventuele algemene observaties of aanvullende feedback..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid="general-remarks-input"
        />
        <div
          className={`text-xs mt-1 text-right ${isNearLimit ? 'text-orange-600' : 'text-gray-500'}`}
          data-testid="character-counter"
        >
          {remainingChars} tekens resterend
        </div>
      </div>
    </section>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * Public Peer Evaluation Page
 *
 * Standalone page accessible via /evaluatie/:token
 * No authentication required - token-based access.
 */
export function PublicPeerEvaluationPage() {
  const { token } = useParams<{ token: string }>();

  // Form state
  const [formState, setFormState] = useState<FormState>({
    taskRemarks: {},
    competencyRemarks: {},
    generalRemarks: '',
  });

  // Validation error state
  const [validationError, setValidationError] = useState<string | null>(null);

  // Page state derived from query/mutation
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Ref for error focus management
  const errorRef = useRef<HTMLDivElement>(null);

  // Query for form data
  const {
    data: formData,
    isLoading,
    isError,
    error,
  } = usePublicPeerEvaluation(token || '');

  // Mutation for submission
  const submitMutation = useSubmitPublicPeerEvaluation();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update document title based on form data
  useEffect(() => {
    if (formData?.employeeName) {
      document.title = `Peer Evaluatie - ${formData.employeeName}`;
    } else {
      document.title = 'Peer Evaluatie';
    }

    // Cleanup: restore default title on unmount
    return () => {
      document.title = 'HR Portal';
    };
  }, [formData?.employeeName]);

  // Focus management for submit errors
  useEffect(() => {
    if (submitMutation.isError && errorRef.current) {
      errorRef.current.focus();
    }
  }, [submitMutation.isError]);

  // Determine page status
  const pageStatus: PageStatus = useMemo(() => {
    if (hasSubmitted) return 'submitted';
    if (submitMutation.isPending) return 'submitting';
    if (isLoading) return 'loading';
    if (isError) {
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage.includes('niet gevonden') || errorMessage.includes('404')) {
        return 'invalid';
      }
      return 'error';
    }
    if (formData) return 'ready';
    return 'loading';
  }, [hasSubmitted, submitMutation.isPending, isLoading, isError, error, formData]);

  // Handlers
  const handleTaskRemarksChange = useCallback((taskId: number, value: string) => {
    setFormState((prev) => ({
      ...prev,
      taskRemarks: { ...prev.taskRemarks, [taskId]: value },
    }));
    // Clear validation error when user types
    setValidationError(null);
  }, []);

  const handleCompetencyRemarksChange = useCallback((competencyId: number, value: string) => {
    setFormState((prev) => ({
      ...prev,
      competencyRemarks: { ...prev.competencyRemarks, [competencyId]: value },
    }));
    // Clear validation error when user types
    setValidationError(null);
  }, []);

  const handleGeneralRemarksChange = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, generalRemarks: value }));
    // Clear validation error when user types
    setValidationError(null);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!token || !formData) return;

      // Validation: at least one text field must be filled
      const hasTaskRemarks = Object.values(formState.taskRemarks).some((r) => r?.trim());
      const hasCompetencyRemarks = Object.values(formState.competencyRemarks).some((r) => r?.trim());
      const hasGeneralRemarks = formState.generalRemarks?.trim();

      if (!hasTaskRemarks && !hasCompetencyRemarks && !hasGeneralRemarks) {
        setValidationError('Vul minimaal één tekstveld in om je evaluatie in te dienen.');
        return;
      }

      const submitData: SubmitPeerEvaluationInput = {
        taskRemarks: formData.tasks.map((t) => ({
          taskId: t.taskId,
          remarks: formState.taskRemarks[t.taskId] || undefined,
        })),
        competencyRemarks: formData.competencies.map((c) => ({
          competencyId: c.competencyId,
          remarks: formState.competencyRemarks[c.competencyId] || undefined,
        })),
        generalRemarks: formState.generalRemarks || undefined,
      };

      submitMutation.mutate(
        { token, data: submitData },
        {
          onSuccess: () => {
            setHasSubmitted(true);
          },
        }
      );
    },
    [token, formData, formState, submitMutation]
  );

  // Render based on page status
  if (pageStatus === 'loading') {
    return <LoadingState />;
  }

  if (pageStatus === 'invalid') {
    return <InvalidState />;
  }

  if (pageStatus === 'error') {
    const errorMessage = error instanceof Error ? error.message : 'Onbekende fout';
    return <ErrorState message={errorMessage} />;
  }

  if (pageStatus === 'submitted') {
    return <SuccessState />;
  }

  // Ready state - show form
  if (!formData) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-peer-evaluation-page">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="page-title">
            Peer Evaluatie voor {formData.employeeName}
          </h1>
          <p className="mt-2 text-gray-600 max-w-xl mx-auto">
            Je bent gevraagd om feedback te geven over de werkprestaties van deze medewerker.
          </p>
        </header>

        {/* Form */}
        <form onSubmit={handleSubmit} data-testid="evaluation-form">
          {/* Validation error */}
          {validationError && (
            <div
              className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm"
              role="alert"
              data-testid="validation-error"
            >
              {validationError}
            </div>
          )}

          {/* Tasks section */}
          <TaskSection
            tasks={formData.tasks}
            remarks={formState.taskRemarks}
            onRemarksChange={handleTaskRemarksChange}
          />

          {/* Competencies section */}
          <CompetencySection
            competencies={formData.competencies}
            remarks={formState.competencyRemarks}
            onRemarksChange={handleCompetencyRemarksChange}
          />

          {/* General remarks */}
          <GeneralRemarksSection
            value={formState.generalRemarks}
            onChange={handleGeneralRemarksChange}
          />

          {/* Submit button */}
          <div className="mt-8">
            <button
              type="submit"
              disabled={pageStatus === 'submitting'}
              className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white font-medium rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="submit-button"
            >
              {pageStatus === 'submitting' ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Indienen...</span>
                  <span className="sr-only">Evaluatie wordt ingediend, even geduld</span>
                </span>
              ) : (
                'Evaluatie Indienen'
              )}
            </button>
          </div>

          {/* Submit error */}
          {submitMutation.isError && (
            <div
              ref={errorRef}
              tabIndex={-1}
              className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
              role="alert"
              data-testid="submit-error"
            >
              <strong>Fout bij indienen:</strong>{' '}
              {submitMutation.error instanceof Error
                ? submitMutation.error.message
                : 'Onbekende fout'}
            </div>
          )}
        </form>

        {/* Footer */}
        <footer className="mt-12 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>Je feedback wordt samen met je naam geregistreerd.</p>
        </footer>
      </div>
    </div>
  );
}
